import type { AppSelectGroup } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppDialog, AppSelect, AppTextarea } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { FIELD_MAX_LENGTHS } from 'shared'
import { Alerts } from './components/Alerts'
import { useCloseReasonDialog } from './hooks/useCloseReasonDialog'

/**
 * CloseReasonDialog コンポーネントの Props
 *
 * @param open - ダイアログの表示状態
 * @param onOpenChange - ダイアログの表示状態変更ハンドラ
 * @param opportunity - 対象オポチュニティ（nullの場合は非表示）
 * @param onSubmit - クローズ理由送信ハンドラ
 */
interface CloseReasonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunity: {
    id: number
    title: string
  } | null
  onSubmit: (data: { opportunityCloseReasonId: number; opportunityAdditionalCloseReason?: string | null }) => void
}

/**
 * オポチュニティクローズ理由ダイアログ
 *
 * オポチュニティをクローズする際のクローズ理由入力ダイアログです。
 */
export function CloseReasonDialog({ open, onOpenChange, opportunity: _opportunity, onSubmit }: CloseReasonDialogProps) {
  const {
    selectedReasonId,
    additionalReason,
    closeReasonError,
    additionalReasonError,
    hasErrors,
    groupedReasons,
    selectedReason,
    isLoading,
    handleReasonChange,
    handleAdditionalReasonChange,
    handleSubmit,
  } = useCloseReasonDialog({ open, onSubmit, onOpenChange })

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title='クローズ理由'
      className='max-w-140'
      footerOptions={[
        { label: 'キャンセル', onClick: () => onOpenChange(false), variant: 'outline' },
        { label: '保存', variant: 'save' as const, onClick: handleSubmit, disabled: hasErrors },
      ]}
    >
      <div className='space-y-4'>
        <Alerts />

        <AppSelect
          id='close-reason'
          label='クローズ理由'
          required
          value={selectedReasonId?.toString() || ''}
          onValueChange={(value) => handleReasonChange(Number.parseInt(value, 10))}
          disabled={isLoading}
          placeholder='クローズ理由を選択してください'
          groups={Array.from(groupedReasons.entries()).map(
            ([category, reasons]): AppSelectGroup => ({
              label: category,
              options: reasons.map((reason) => ({
                value: reason.id.toString(),
                label: reason.closeReason,
              })),
            }),
          )}
          error={closeReasonError ?? undefined}
        />

        {selectedReason?.isAdditionalCloseReasonRequired && (
          <AppTextarea
            id='additional-reason'
            label='クローズ理由（詳細）'
            required
            value={additionalReason}
            onChange={handleAdditionalReasonChange}
            placeholder='クローズ理由の詳細を入力してください'
            rows={3}
            maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION}
            showCharCount
            limitHeight='md'
            error={additionalReasonError ?? undefined}
          />
        )}
      </div>
    </AppDialog>
  )
}
